"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { useReducedMotion } from "motion/react";
import { useCallback, useMemo } from "react";
import { useDetectTouchDevice } from "../../../hooks/index.js";
import { cn } from "../../../utils/cn.js";
import { HeroContext } from "../../Hero/HeroContext.js";
import { carouselVariants } from "../Carousel.variants.js";
import { CarouselControls } from "../CarouselControls.js";
import { useCarouselEngine } from "../useCarouselEngine.js";
import { heroCarouselVariants } from "./hero.variants.js";
import { HeroSlide } from "./HeroSlide.js";
import { normalizeSlideChildren } from "./normalizeSlideChildren.js";
import { useHeroArrowPlacement } from "./useHeroArrowPlacement.js";
function HeroCarouselView({ children, ariaLabel, display = 'splitWithImage', emphasis = 'regular', imagePosition = 'topToRight', backgroundVariant = 'solid', pagination = 'dots', arrows = true, loop = true, startIndex = 0, onSlideChange, plugins: pluginsProp, carouselId: carouselIdProp, slotClassNames = {} }) {
    const isTouchDevice = useDetectTouchDevice();
    const prefersReducedMotion = useReducedMotion() ?? false;
    const slideElements = useMemo(()=>normalizeSlideChildren(children), [
        children
    ]);
    const slideCount = slideElements.length;
    const { emblaCallbackRef, selectedIndex, onDotButtonClick, prevBtnDisabled, nextBtnDisabled, prevBtnHidden, onPrevButtonClick, onNextButtonClick, autoplayIsPlaying, toggleAutoplay, resetAutoplayOnInteraction, handleMouseEnter, handleMouseLeave, autoplayInViewRef, hasAutoplay } = useCarouselEngine({
        loop,
        startIndex,
        plugins: pluginsProp,
        carouselIdProp,
        onSlideChange,
        prefersReducedMotion
    });
    const showArrowBtns = arrows;
    const showDotBtns = 'dots' === pagination;
    const showSlideNum = 'numbers' === pagination;
    const showBlurBox = showSlideNum || hasAutoplay;
    const { containerRef, arrowPlacement, arrowIconButtonProps } = useHeroArrowPlacement({
        showArrowBtns,
        isTouchDevice
    });
    const heroContextValue = useMemo(()=>({
            display,
            emphasis,
            imagePosition,
            backgroundVariant
        }), [
        display,
        emphasis,
        imagePosition,
        backgroundVariant
    ]);
    const slidesForControls = useMemo(()=>slideElements.map((slide, index)=>({
                id: slide.key ?? `slide-${index}`,
                title: slide.props.ariaLabel
            })), [
        slideElements
    ]);
    const onLightboxButtonClick = (callback)=>callback();
    const { wrapperParentClass = '', wrapperClass = '', rootClass = '', viewportClass = '', containerClass = '', slideClass = '', buttonClass = '', dotsPaginationClass = '', dotButtonClass = '', dotClass = '', blurBoxClass = '', blurBoxDividerClass = '', blurBoxNumbersClass = '', blurBoxAutoplayButtonClass = '' } = slotClassNames;
    const { wrapperParentSlot, wrapperSlot, rootSlot, viewportSlot, containerSlot, prevButtonSlot, nextButtonSlot, hiddenButtonSlot, dotsPaginationSlot, dotButtonSlot, slideSlot } = carouselVariants({
        type: 'hero',
        slideWidth: 'image',
        bleed: 'defaultBleed',
        rounded: 'none',
        loop,
        hideArrowsOnMobileOrTouch: isTouchDevice ? 'always' : 'never'
    });
    const { wrapperParentSlot: heroWrapperParentSlot, prevButtonSlot: heroPrevButtonSlot, nextButtonSlot: heroNextButtonSlot, hiddenButtonSlot: heroHiddenButtonSlot, bottomControlRowSlot, dotsPaginationSlot: heroDotsPaginationBaseSlot } = heroCarouselVariants({
        isTouchDevice
    });
    const heroArrowPrevSlot = ()=>cn(prevButtonSlot(), heroPrevButtonSlot());
    const heroArrowNextSlot = ()=>cn(nextButtonSlot(), heroNextButtonSlot());
    const heroArrowHiddenSlot = ()=>cn(hiddenButtonSlot(), heroHiddenButtonSlot());
    const heroBottomControlRowSlot = ()=>bottomControlRowSlot();
    const heroDotsPaginationSlot = ()=>cn(dotsPaginationSlot(), heroDotsPaginationBaseSlot());
    const wrapperParentClasses = cn(heroWrapperParentSlot(), wrapperParentSlot(), wrapperParentClass);
    const wrapperClasses = cn(wrapperSlot(), wrapperClass);
    const rootClasses = cn(rootSlot(), rootClass);
    const viewportClasses = cn(viewportSlot(), viewportClass);
    const containerClasses = cn(containerSlot(), containerClass);
    const slideClasses = cn(slideSlot(), slideClass);
    const currentSlide = selectedIndex + 1;
    const totalSlides = slideCount;
    const onPrevNextButtonClick = useCallback((callback)=>resetAutoplayOnInteraction(callback), [
        resetAutoplayOnInteraction
    ]);
    const controlsConfig = {
        showArrowBtns,
        showDotBtns,
        showBlurBox,
        showSlideNum,
        isLightBoxSupported: false,
        hasAutoplay,
        isImageCarousel: false,
        arrowButtonType: 'content',
        arrowPlacement
    };
    return /*#__PURE__*/ jsx("div", {
        ref: containerRef,
        "data-testid": "carousel-wrapper-parent",
        className: wrapperParentClasses,
        children: /*#__PURE__*/ jsx("div", {
            "data-testid": "carousel-wrapper",
            className: wrapperClasses,
            children: /*#__PURE__*/ jsxs("section", {
                "aria-label": ariaLabel,
                "data-testid": "carousel-root",
                className: rootClasses,
                onMouseEnter: handleMouseEnter,
                onMouseLeave: handleMouseLeave,
                ref: autoplayInViewRef,
                children: [
                    /*#__PURE__*/ jsx(HeroContext.Provider, {
                        value: heroContextValue,
                        children: /*#__PURE__*/ jsx("div", {
                            "data-testid": "carousel-viewport",
                            className: viewportClasses,
                            ref: emblaCallbackRef,
                            children: /*#__PURE__*/ jsx("ul", {
                                "data-testid": "carousel-container",
                                className: containerClasses,
                                children: slideElements.map((slide, index)=>{
                                    const isSelectedSlide = index === selectedIndex;
                                    return /*#__PURE__*/ jsx(HeroSlide, {
                                        ariaLabel: slide.props.ariaLabel,
                                        isSelected: isSelectedSlide,
                                        index: index,
                                        className: slideClasses,
                                        children: slide.props.children
                                    }, slide.key ?? index);
                                })
                            })
                        })
                    }),
                    /*#__PURE__*/ jsx(CarouselControls, {
                        config: controlsConfig,
                        slides: slidesForControls,
                        selectedIndex: selectedIndex,
                        prevBtnDisabled: prevBtnDisabled,
                        nextBtnDisabled: nextBtnDisabled,
                        prevBtnHidden: prevBtnHidden,
                        onPrevButtonClick: onPrevButtonClick,
                        onNextButtonClick: onNextButtonClick,
                        onDotButtonClick: onDotButtonClick,
                        onPrevNextButtonClick: onPrevNextButtonClick,
                        resetAutoplayOnInteraction: resetAutoplayOnInteraction,
                        onLightboxButtonClick: onLightboxButtonClick,
                        toggleAutoplay: toggleAutoplay,
                        autoplayIsPlaying: autoplayIsPlaying,
                        slotClassNames: {
                            buttonClass,
                            dotsPaginationClass,
                            dotButtonClass,
                            dotClass,
                            blurBoxClass,
                            blurBoxDividerClass,
                            blurBoxNumbersClass,
                            blurBoxLightboxButtonClass: '',
                            blurBoxAutoplayButtonClass
                        },
                        variantSlots: {
                            prevButtonSlot: heroArrowPrevSlot,
                            nextButtonSlot: heroArrowNextSlot,
                            hiddenButtonSlot: heroArrowHiddenSlot,
                            dotsPaginationSlot: heroDotsPaginationSlot,
                            dotButtonSlot,
                            bottomControlRowSlot: heroBottomControlRowSlot
                        },
                        currentSlide: currentSlide,
                        totalSlides: totalSlides,
                        arrowIconButtonProps: arrowIconButtonProps
                    })
                ]
            })
        })
    });
}
export { HeroCarouselView };
